import { trackConversion } from "@/lib/analytics";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { submitCallback } from "@/lib/callback.functions";

export function FooterCallback() {
  const { t } = useTranslation();
  const callFn = useServerFn(submitCallback);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    setError(null);
    try {
      // Ta med besökar-/sessions-id så leadet kan kopplas till hela sajtresan.
      const { getAttribSnapshot } = await import("@/lib/analytics/track");
      const attrib = getAttribSnapshot();
      const res = await callFn({
        data: {
          name: name.trim().slice(0, 100),
          phone: phone.trim().slice(0, 32),
          visitor_id: attrib.visitor_id,
          session_id: attrib.session_id,
          source_path:
            typeof window !== "undefined" ? window.location.pathname : null,
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
        },
      });
      if (res.ok) {
        setState("ok");
        setName("");
        setPhone("");
        trackConversion({
          conversion_type: "callback_request",
          location: "footer",
          label: "Ring upp mig",
        });
      } else {
        setState("error");
        setError(res.error);
      }
    } catch (err) {
      setState("error");
      setError(err instanceof Error ? err.message : t("callback.genericError"));
    }
  }

  return (
    <form data-track-form="ring-upp-mig" onSubmit={onSubmit} className="w-full" aria-label={t("callback.aria") as string}>
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
        <label htmlFor="cb-name" className="sr-only">{t("callback.name")}</label>
        <input
          id="cb-name"
          type="text"
          autoComplete="name"
          required
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("callback.firstName") as string}
          className="h-12 flex-1 rounded-full border border-white/15 bg-background/60 px-5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-mint focus:outline-none focus:ring-2 focus:ring-mint/30 sm:max-w-[10rem]"
        />
        <label htmlFor="cb-phone" className="sr-only">{t("callback.phone")}</label>
        <input
          id="cb-phone"
          type="tel"
          autoComplete="tel"
          required
          maxLength={32}
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t("callback.phonePlaceholder") as string}
          className="h-12 flex-1 rounded-full border border-white/15 bg-background/60 px-5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-mint focus:outline-none focus:ring-2 focus:ring-mint/30"
        />
        <button
          type="submit"
          disabled={state === "loading" || state === "ok"}
          className="inline-flex h-12 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-mint px-6 text-sm font-semibold text-black transition-colors hover:bg-mint/90 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {state === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
          {state === "ok" && <Check className="h-4 w-4" />}
          {state === "idle" || state === "error" ? (
            <>
              {t("callback.callMe")} <ArrowRight className="h-4 w-4" />
            </>
          ) : state === "loading" ? (
            t("callback.sending")
          ) : (
            t("callback.queued")
          )}
        </button>
      </div>

      {state === "error" && error && (
        <p role="alert" className="mt-2 text-xs text-rose-300">
          {error}
        </p>
      )}
      {state === "ok" && (
        <p role="status" className="mt-2 text-xs text-mint">
          {t("callback.thanks")}
        </p>
      )}
    </form>
  );
}
